/**
 * Branding utilities for managing user-specific branding and themes
 */

export interface BrandingConfig {
  // Primary colors
  primaryColor: string
  primaryColorLight: string
  primaryColorDark: string
  
  // Secondary colors
  secondaryColor: string
  secondaryColorLight: string
  secondaryColorDark: string
  
  // Neutral colors
  backgroundColor: string
  surfaceColor: string
  textColor: string
  textSecondaryColor: string
  
  // Brand assets
  brandName: string
  logoUrl?: string
  faviconUrl?: string
  authorImageUrl?: string
  
  // Theme settings
  theme: 'dark' | 'light'
  borderRadius: string
  fontFamily: string
  
  // White-label merchant info
  supportEmail?: string
  supportPhone?: string
  pageTitle?: string
  metaDescription?: string
  
  // Custom CSS variables
  customVariables?: Record<string, string>
}

export interface DefaultBrandingConfig extends BrandingConfig {
  // Default PexiBlock branding
  primaryColor: '#FF9902'
  primaryColorLight: '#ffb84d'
  primaryColorDark: '#e68a00'
  secondaryColor: '#2E3192'
  secondaryColorLight: '#4a4db8'
  secondaryColorDark: '#25287a'
  backgroundColor: '#1f2937'
  surfaceColor: '#374151'
  textColor: '#ffffff'
  textSecondaryColor: '#9ca3af'
  brandName: 'PEXIBLOCK'
  theme: 'dark'
  borderRadius: '0.75rem'
  fontFamily: '"Alan Sans", sans-serif'
}

// Default PexiBlock branding configuration
export const DEFAULT_BRANDING: DefaultBrandingConfig = {
  primaryColor: '#FF9902',
  primaryColorLight: '#ffb84d',
  primaryColorDark: '#e68a00',
  secondaryColor: '#2E3192',
  secondaryColorLight: '#4a4db8',
  secondaryColorDark: '#25287a',
  backgroundColor: '#1f2937',
  surfaceColor: '#374151',
  textColor: '#ffffff',
  textSecondaryColor: '#9ca3af',
  brandName: 'PEXIBLOCK',
  theme: 'dark',
  borderRadius: '0.75rem',
  fontFamily: '"Alan Sans", sans-serif',
}

/**
 * Generate CSS variables from branding configuration
 */
export function generateCSSVariables(branding: BrandingConfig): Record<string, string> {
  const variables: Record<string, string> = {
    // Primary colors
    '--pexi-orange': branding.primaryColor,
    '--pexi-orange-light': branding.primaryColorLight,
    '--pexi-orange-dark': branding.primaryColorDark,
    
    // Secondary colors
    '--pexi-blue': branding.secondaryColor,
    '--pexi-blue-light': branding.secondaryColorLight,
    '--pexi-blue-dark': branding.secondaryColorDark,
    
    // Neutral colors
    '--pexi-dark': branding.backgroundColor,
    '--pexi-gray': branding.textSecondaryColor,
    '--pexi-light-gray': branding.surfaceColor,
    '--pexi-white': branding.textColor,
    
    // Brand gradients
    '--pexi-orange-gradient': `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.primaryColorLight} 100%)`,
    '--pexi-blue-gradient': `linear-gradient(135deg, ${branding.secondaryColor} 0%, ${branding.secondaryColorLight} 100%)`,
    '--pexi-dual-gradient': `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`,
    
    // Theme-specific variables
    '--pexi-bg-primary': branding.backgroundColor,
    '--pexi-bg-secondary': branding.surfaceColor,
    '--pexi-text-primary': branding.textColor,
    '--pexi-text-secondary': branding.textSecondaryColor,
    
    // Border radius
    '--pexi-border-radius': branding.borderRadius,
    
    // Font family
    '--pexi-font-family': branding.fontFamily,
  }
  
  // Add custom variables if provided
  if (branding.customVariables) {
    Object.assign(variables, branding.customVariables)
  }
  
  return variables
}

/**
 * Apply branding configuration to the document root
 */
export function applyBranding(branding: BrandingConfig): void {
  const root = document.documentElement
  const variables = generateCSSVariables(branding)
  
  // Apply CSS variables to root element
  Object.entries(variables).forEach(([key, value]) => {
    root.style.setProperty(key, value)
  })
  
  // Apply theme class to body
  document.body.className = document.body.className.replace(/theme-\w+/g, '')
  document.body.classList.add(`theme-${branding.theme}`)
  
  // Update document title with brand name
  if (branding.brandName && branding.brandName !== 'PEXIBLOCK') {
    const originalTitle = document.title.replace(/^.*? - /, '')
    document.title = `${branding.brandName} - ${originalTitle}`
  }
}

/**
 * Create branding configuration from user data
 */
export function createBrandingFromUser(user: {
  brand_color?: string
  theme?: 'dark' | 'light'
  name?: string
  image_url?: string
  author_image_url?: string
}): BrandingConfig {
  const primaryColor = user.brand_color || DEFAULT_BRANDING.primaryColor
  
  // Generate complementary colors based on primary color
  const { light: primaryLight, dark: primaryDark } = generateColorVariants(primaryColor)
  
  return {
    ...DEFAULT_BRANDING,
    primaryColor,
    primaryColorLight: primaryLight,
    primaryColorDark: primaryDark,
    theme: user.theme || DEFAULT_BRANDING.theme,
    brandName: user.name || DEFAULT_BRANDING.brandName,
    logoUrl: user.image_url,
    authorImageUrl: user.author_image_url,
  }
}

/**
 * Generate light and dark variants of a color
 */
export function generateColorVariants(color: string): { light: string; dark: string } {
  // Convert hex to RGB
  const hex = color.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)
  
  // Generate light variant (increase brightness)
  const lightR = Math.min(255, Math.floor(r + (255 - r) * 0.3))
  const lightG = Math.min(255, Math.floor(g + (255 - g) * 0.3))
  const lightB = Math.min(255, Math.floor(b + (255 - b) * 0.3))
  
  // Generate dark variant (decrease brightness)
  const darkR = Math.max(0, Math.floor(r * 0.8))
  const darkG = Math.max(0, Math.floor(g * 0.8))
  const darkB = Math.max(0, Math.floor(b * 0.8))
  
  return {
    light: `#${lightR.toString(16).padStart(2, '0')}${lightG.toString(16).padStart(2, '0')}${lightB.toString(16).padStart(2, '0')}`,
    dark: `#${darkR.toString(16).padStart(2, '0')}${darkG.toString(16).padStart(2, '0')}${darkB.toString(16).padStart(2, '0')}`,
  }
}

/**
 * Validate color format (hex)
 */
export function isValidColor(color: string): boolean {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)
}

/**
 * Get contrast ratio between two colors
 */
export function getContrastRatio(color1: string, color2: string): number {
  const getLuminance = (color: string) => {
    const hex = color.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16) / 255
    const g = parseInt(hex.substr(2, 2), 16) / 255
    const b = parseInt(hex.substr(4, 2), 16) / 255
    
    const [rs, gs, bs] = [r, g, b].map(c => 
      c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    )
    
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
  }
  
  const lum1 = getLuminance(color1)
  const lum2 = getLuminance(color2)
  
  return (Math.max(lum1, lum2) + 0.05) / (Math.min(lum1, lum2) + 0.05)
}

/**
 * Check if color combination meets accessibility standards
 */
export function isAccessibleColorCombination(foreground: string, background: string): boolean {
  const ratio = getContrastRatio(foreground, background)
  return ratio >= 4.5 // WCAG AA standard
}

/**
 * Generate accessible color suggestions
 */
export function generateAccessibleColors(_primaryColor: string, theme: 'dark' | 'light'): {
  textColor: string
  backgroundColor: string
  surfaceColor: string
} {
  const isDark = theme === 'dark'
  
  if (isDark) {
    return {
      textColor: '#ffffff',
      backgroundColor: '#1f2937',
      surfaceColor: '#374151',
    }
  } else {
    return {
      textColor: '#1f2937',
      backgroundColor: '#ffffff',
      surfaceColor: '#f9fafb',
    }
  }
}

/**
 * Reset branding to default
 */
export function resetToDefaultBranding(): void {
  applyBranding(DEFAULT_BRANDING)
}

/**
 * Save branding configuration to localStorage
 */
export function saveBrandingToStorage(branding: BrandingConfig): void {
  try {
    localStorage.setItem('pexiblocks-branding', JSON.stringify(branding))
  } catch (error) {
    console.warn('Failed to save branding to localStorage:', error)
  }
}

/**
 * Load branding configuration from localStorage
 */
export function loadBrandingFromStorage(): BrandingConfig | null {
  try {
    const stored = localStorage.getItem('pexiblocks-branding')
    return stored ? JSON.parse(stored) : null
  } catch (error) {
    console.warn('Failed to load branding from localStorage:', error)
    return null
  }
}

/**
 * Clear branding from localStorage
 */
export function clearBrandingFromStorage(): void {
  try {
    localStorage.removeItem('pexiblocks-branding')
  } catch (error) {
    console.warn('Failed to clear branding from localStorage:', error)
  }
}

/**
 * Get branding preview styles for preview components
 */
export function getBrandingPreviewStyles(branding: BrandingConfig): React.CSSProperties {
  return {
    '--pexi-orange': branding.primaryColor,
    '--pexi-orange-light': branding.primaryColorLight,
    '--pexi-orange-dark': branding.primaryColorDark,
    '--pexi-blue': branding.secondaryColor,
    '--pexi-blue-light': branding.secondaryColorLight,
    '--pexi-blue-dark': branding.secondaryColorDark,
    '--pexi-dual-gradient': `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`,
  } as React.CSSProperties
}